import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type { Home } from '@lmc/ts-common'
import type { FollowUpActionEntry } from '@lmc/validations'
import * as Sentry from '@sentry/react'
import { Form as AntdForm } from 'antd'
import { isApiError } from 'common/services/apiError'
import { getNominative, showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  attachmentsLeftAfterFailedDelete,
  deleteCaseAttachments,
  type IncidentCaseAttachment,
  removedKeys,
  uploadCaseAttachments,
  uploadFailure,
  useCaseReportCache
} from '../attachments/caseAttachments'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepHandle,
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import FollowUpActionList from './followUpActions/FollowUpActionList'
import {
  type FollowUpActionValue,
  toActionRequest
} from './followUpActions/followUpActionValues'

const testId = 'incident-case-v2-follow-up-actions'

type FollowUpActionsValues = {
  actions: Array<FollowUpActionValue>
}

/**
 * A saved action's files. Not on the published `FollowUpActionEntry` yet;
 * switch to it once `@lmc/validations` is bumped past the release adding them.
 */
type SavedAction = FollowUpActionEntry & {
  attachments?: Array<IncidentCaseAttachment>
}

/** The saved follow-up as the read returns it; absent until the step is saved. */
type SavedFollowUp = {
  followUpActions?: {
    actions?: Array<SavedAction>
    notificationRequired?: boolean
    safeguardingActionsRequired?: boolean
  } | null
}

/**
 * Step 6: the actions that follow the incident, each with an owner, a due date
 * and a status, added from scratch or from a template.
 *
 * `followUpActions` is saved whole. The two flags beside the actions are not in
 * this step's design, so they go back as they were saved.
 *
 * An action's files are not part of the step: the drawer only queues picks and
 * removals, as it does every other edit, and Next applies them. A deleted
 * action's files are deleted before the save, which the server requires
 * before it lets the action go; other removals and the uploads follow the
 * save, since a new action has no id until then.
 */
const FollowUpActionsStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const saved = (incidentReport.case as SavedFollowUp | null)?.followUpActions
  const notificationRequired = saved?.notificationRequired ?? false
  const safeguardingActionsRequired =
    saved?.safeguardingActionsRequired ?? false

  const initialValues = useMemo<FollowUpActionsValues>(
    () => ({
      actions: (saved?.actions ?? []).map(({ attachments, ...action }) => ({
        ...toActionRequest(action),
        attachments: { saved: attachments ?? [], queued: [] }
      }))
    }),
    [saved]
  )
  const [form] = AntdForm.useForm<FollowUpActionsValues>()
  const { readSavedCase, refreshReport } = useCaseReportCache(incidentReport)
  // Each saved action's files as the server last reported them.
  const storedFiles = useRef(
    new Map(
      (saved?.actions ?? []).flatMap((action) =>
        action.id ? [[action.id, action.attachments ?? []] as const] : []
      )
    )
  )

  // The form's actions when Next sent them, in the order sent.
  const sentSnapshot = useRef<Array<FollowUpActionValue>>([])

  const currentActions = (): Array<FollowUpActionValue> =>
    form.getFieldValue('actions') ?? []

  /** Files removed in the drawer, or with their action, by action id. */
  const removals = () => {
    const actions = currentActions()
    return [...storedFiles.current].flatMap(([actionId, files]) => {
      const kept =
        actions.find((action) => action.id === actionId)?.attachments?.saved ??
        []
      const keys = removedKeys(files, kept)
      return keys.length ? [{ actionId, keys }] : []
    })
  }

  const hasPendingChanges = () =>
    removals().length > 0 ||
    currentActions().some((action) => action.attachments?.queued.length)

  const setAction = (index: number, action: FollowUpActionValue) =>
    form.setFieldValue(
      'actions',
      currentActions().map((other, at) => (at === index ? action : other))
    )

  const reportFailure = (
    error: unknown,
    { key, values }: { key: string; values?: Record<string, number> }
  ) => {
    if (error && !isApiError(error)) {
      Sentry.captureException(error)
    }
    showToast({ type: 'error', title: t(key, values) })
  }

  /**
   * Deletes an action's files by key. On failure the files still stored are
   * listed again, and the error is returned for the caller to report.
   */
  const deleteFiles = async (actionId: string, keys: Array<string>) => {
    const target = { kind: 'action', actionId } as const
    try {
      storedFiles.current.set(
        actionId,
        await deleteCaseAttachments(incidentReport.id, target, keys)
      )
      return null
    } catch (error) {
      const left = attachmentsLeftAfterFailedDelete(error)
      if (left) {
        storedFiles.current.set(actionId, left)
        const index = currentActions().findIndex(
          (action) => action.id === actionId
        )
        const action = currentActions()[index]
        if (action) {
          setAction(index, {
            ...action,
            attachments: {
              queued: action.attachments?.queued ?? [],
              saved: left
            }
          })
        }
      }
      return error
    }
  }

  /**
   * Only the files of deleted actions go before the save: the server refuses
   * to drop an action that still has files. Files removed from an action
   * that stays go after it, so a failed save loses none.
   */
  const beforeSave = async () => {
    sentSnapshot.current = currentActions()
    const deleted = removals().filter(
      ({ actionId }) =>
        !currentActions().some((action) => action.id === actionId)
    )
    try {
      for (const { actionId, keys } of deleted) {
        const error = await deleteFiles(actionId, keys)
        if (error) {
          reportFailure(error, {
            key: incidentReportsConstants.CASE_V2_ATTACHMENTS_DELETE_FAILED
          })
          return false
        }
      }
      return true
    } finally {
      if (deleted.length) {
        refreshReport()
      }
    }
  }

  const afterSave: NonNullable<IncidentCaseV2StepHandle['afterSave']> = async (
    sentValues
  ) => {
    const sentActions = sentValues.followUpActions?.actions ?? []
    // The list as sent, so pairing is by the same positions the server saw.
    const snapshot =
      sentSnapshot.current.length === sentActions.length
        ? sentSnapshot.current
        : []
    // Each sent action as it now is in the form; edits replace entries.
    const live = [...snapshot]
    const updateEntry = (
      index: number,
      update: (action: FollowUpActionValue) => FollowUpActionValue
    ) => {
      const at = currentActions().indexOf(live[index])
      if (at === -1) {
        return false
      }
      live[index] = update(live[index])
      setAction(at, live[index])
      return true
    }
    const uploads = snapshot.flatMap((action, index) =>
      action.attachments?.queued.length ? [{ action, index }] : []
    )
    const removed = snapshot.flatMap((action) => {
      const keys = action.id
        ? removedKeys(
            storedFiles.current.get(action.id) ?? [],
            action.attachments?.saved ?? []
          )
        : []
      return action.id && keys.length ? [{ actionId: action.id, keys }] : []
    })
    // One message, for the first thing that failed.
    let failure: {
      error: unknown
      key: string
      values?: Record<string, number>
    } | null = null
    for (const { actionId, keys } of removed) {
      const error = await deleteFiles(actionId, keys)
      if (error && !failure) {
        failure = {
          error,
          key: incidentReportsConstants.CASE_V2_ATTACHMENTS_DELETE_FAILED
        }
      }
    }
    // A saved action keeps its own id; a new one takes the saved case's.
    let ids = snapshot.map((action) => action.id)
    let isDone = true
    try {
      if (uploads.some(({ action }) => !action.id)) {
        const savedCase = (await readSavedCase()) as SavedFollowUp | null
        const stored = savedCase?.followUpActions?.actions ?? []
        ids = snapshot.map(
          (action, index) =>
            action.id ??
            (stored[index]?.title === sentActions[index]?.title
              ? stored[index]?.id
              : undefined)
        )
        // Into the form before any upload, so a retry sends the same ids
        // rather than having the server mint new actions.
        snapshot.forEach((action, index) => {
          const id = ids[index]
          if (!action.id && id) {
            updateEntry(index, (current) => ({ ...current, id }))
          }
        })
      }
      for (const { action, index } of uploads) {
        const actionId = ids[index]
        // No saved action to attach to, or the user has since deleted or
        // edited it: its files stay queued, and the user is told.
        if (!actionId || !currentActions().includes(live[index])) {
          failure ??= {
            error: null,
            key: incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD_SKIPPED
          }
          continue
        }
        const files = await uploadCaseAttachments(
          incidentReport.id,
          { kind: 'action', actionId },
          action.attachments?.queued ?? []
        )
        storedFiles.current.set(actionId, files)
        updateEntry(index, (current) => ({
          ...current,
          id: actionId,
          attachments: {
            queued: (current.attachments?.queued ?? []).filter(
              (file) => !action.attachments?.queued.includes(file)
            ),
            saved: files
          }
        }))
      }
    } catch (error) {
      failure ??= { error, ...uploadFailure(error) }
    } finally {
      if (uploads.length || removed.length) {
        refreshReport()
      }
    }
    if (failure) {
      reportFailure(failure.error, failure)
      isDone = false
    }
    // Ids written to the form are saved ones, so they don't read as edits.
    return {
      isDone,
      savedValues: {
        ...sentValues,
        followUpActions: sentValues.followUpActions && {
          ...sentValues.followUpActions,
          actions: sentActions.map((action, index) => {
            const id = live[index]?.id
            return !action.id && id
              ? toActionRequest({ ...action, id })
              : action
          })
        }
      }
    }
  }

  const toStepValues = (
    values: FollowUpActionsValues
  ): IncidentCaseV2StepValues => ({
    followUpActions: {
      actions: values.actions.map(toActionRequest),
      notificationRequired,
      safeguardingActionsRequired
    }
  })

  const templateValues = {
    carePlan: getNominative(
      home?.preferredCarePlansNominative,
      t(incidentReportsConstants.CASE_V2_ACTIONS_CARE_PLAN)
    ),
    name: incidentReport.serviceUser.name ?? ''
  }

  return (
    <IncidentCaseV2StepForm
      form={form}
      hasPendingChanges={hasPendingChanges}
      beforeSave={beforeSave}
      afterSave={afterSave}
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <AntdForm.Item name="actions" noStyle>
        <FollowUpActionList
          isSafeguarding={incidentReport.log.item?.id === SAFEGUARDING_ITEM_ID}
          templateValues={templateValues}
          testId={testId}
        />
      </AntdForm.Item>
    </IncidentCaseV2StepForm>
  )
}

export default FollowUpActionsStep
